import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

// Modifica StyledTableCell per includere la gestione dell'overflow
const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#004652',
        color: '#fff',
        // Stili aggiunti per header
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 13,
        // Stili aggiunti per celle corpo
        whiteSpace: 'nowrap', // Non mandare a capo il testo
        overflow: 'hidden', // Nascondi l'eccesso
        textOverflow: 'ellipsis', // Mostra '...'
        // Aggiungi font stack per supportare meglio i simboli greci
        fontFamily: '"Titillium", Times, serif, "DejaVu Sans", "Titillium", Arial, sans-serif',
    },
    // Riduciamo un po' il padding per guadagnare spazio
    padding: '8px 10px', // Adatta se necessario

    // Stili per le righe di stato
    '&.status-verified-row': {
        backgroundColor: '#20e61c',
        color: 'white',
        textAlign: 'center',
        fontWeight: 'normal',
        paddingTop: '3px',
        paddingBottom: '3px',
    },
    '&.status-not-verified-row': {
        backgroundColor: 'red',
        color: 'white',
        textAlign: 'center',
        paddingTop: '3px',
        paddingBottom: '3px',
    },
    i: {
        marginLeft: '15px',
        fontSize: '15px',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

interface TableProps {
    columns: string[];
    data: (string | React.ReactNode)[][];
    maxHeight?: string; // Opzionale, per limitare l'altezza della tabella
}

const CustomTable = (props: TableProps) => {
    const verifiedRowMarker = 'VERIFIED_ROW::';
    const notVerifiedRowMarker = 'NOT_VERIFIED_ROW::';

    return (
        <TableContainer component={Paper} sx={{ maxHeight: props.maxHeight, overflow: props.maxHeight ? 'auto' : 'visible' }}>
            {/* Applica maxHeight e overflow auto */}
            <Table
                sx={{
                    // Rimuovi minWidth: 700
                    tableLayout: 'fixed', // Usa layout fisso
                    width: '100%', // Occupa il 100% del contenitore (Grid item)
                    wordWrap: 'break-word', // Sicurezza per parole molto lunghe

                    // Definisci qui le larghezze delle colonne (target sia th che td)
                    // Adatta queste percentuali come preferisci!
                    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: '50%' }, // Colonna 1 (Descrizione)
                    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: '15%' }, // Colonna 2 (Simbolo)
                    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' }, // Colonna 3 (Valore)
                    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: '15%' }, // Colonna 4 (Unità)
                }}
                aria-label="customized table">
                <TableHead>
                    <TableRow>
                        {props.columns.map((column, index) => (
                            // La larghezza è ora definita nella prop sx della Table
                            <StyledTableCell key={index}>{column}</StyledTableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.data.map((row, rowIndex) => {
                        // Se la riga ha un solo elemento, usa colspan massimo
                        if (row.length === 1) {
                            const cellContent = row[0];
                            let cellClassName = '';

                            // Gestione classi per status row
                            if (typeof cellContent === 'string' && cellContent.startsWith(verifiedRowMarker)) {
                                cellClassName = 'status-verified-row';
                            } else if (typeof cellContent === 'string' && cellContent.startsWith(notVerifiedRowMarker)) {
                                cellClassName = 'status-not-verified-row';
                            }

                            // Se status row, rimuovi il marker dal testo
                            const displayText =
                                typeof cellContent === 'string' && cellContent.startsWith(verifiedRowMarker)
                                    ? cellContent.substring(verifiedRowMarker.length)
                                    : typeof cellContent === 'string' && cellContent.startsWith(notVerifiedRowMarker)
                                        ? cellContent.substring(notVerifiedRowMarker.length)
                                        : cellContent;

                            return (
                                <StyledTableRow key={`singlecell-row-${rowIndex}`}>
                                    <StyledTableCell colSpan={props.columns.length} className={cellClassName}>
                                        {displayText}
                                        {typeof cellContent === 'string' && cellContent.startsWith(verifiedRowMarker) ? <i className="fa-solid fa-check"></i> : null}
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        }

                        // Reason: Handle product rows with 2 elements (description + value spanning 3 columns)
                        if (row.length === 2) {
                            return (
                                <StyledTableRow key={`product-row-${rowIndex}`}>
                                    <StyledTableCell>{row[0]}</StyledTableCell>
                                    <StyledTableCell colSpan={3}>{row[1]}</StyledTableCell>
                                </StyledTableRow>
                            );
                        }

                        // Altrimenti, renderizza una riga normale
                        return (
                            <StyledTableRow key={`data-row-${rowIndex}`}>
                                {row.map((cell, cellIndex) => (
                                    <StyledTableCell key={`cell-${rowIndex}-${cellIndex}`}>{cell}</StyledTableCell>
                                ))}
                            </StyledTableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CustomTable;
